import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { Typography } from "@progress/kendo-react-common";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import FormTextField from "../../components/formFields/FormTextField";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import RippleButton from "../../components/common/RippleButton";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonForAll from "../../components/common/ButtonForAll";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  createServiceLocation,
  getServiceLocationByID,
  updateServiceLocation,
} from "./services/serviceLocation.services";
import { clearServiceLocationDetails } from "./serviceLocationSlice";
import ServiceInfoArray from "./components/ServiceInfoArray";
import { FieldArray } from "@progress/kendo-react-form";
import FormMultiSelectionFiled from "../../components/formFields/FormMultiSelectionFiled";
import { getAllActiveSectors } from "../sector/services/sector.services";

const CreateServiceLocation: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const ServiceLocationID = location.state?.ServiceLocationID;
  const gridRef = useRef<any>(null);
  const dispatch = useAppDispatch();

  const loading = useAppSelector((state) => state.serviceLocation.loading);
  const details = useAppSelector((state) => state.serviceLocation.ServiceLocationDetails);
  const SectorList = useAppSelector((state) => state.sector.SectorList);

  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    dispatch(getAllActiveSectors());
    if (ServiceLocationID) {
      dispatch(getServiceLocationByID(ServiceLocationID));
    }
    return () => {
      dispatch(clearServiceLocationDetails());
    };
  }, [ServiceLocationID]);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [details]);

  const handleSubmit = async (values: any) => {
    const payload = {
      ...values,
      ID: ServiceLocationID ? ServiceLocationID : undefined,
      IsActive: values.IsActive === 1,
      // Convert array of IDs to comma-separated string
      DestinationIDs: Array.isArray(values.DestinationIDs)
        ? values.DestinationIDs.join(",")
        : values.DestinationIDs,
      // Ensure ServiceInfo is present
      ServiceInfo: values.ServiceInfo || []
    };

    if (ServiceLocationID) {
      const response = await dispatch(updateServiceLocation(payload));
      if (response?.meta?.requestStatus === "fulfilled") {
        navigate("/servicelocation");
      }
    } else {
      const response = await dispatch(createServiceLocation(payload));
      if (response?.meta?.requestStatus === "fulfilled") {
        navigate("/servicelocation");
      }
    }
  };

  const initialValues = details ? {
    ...details,
    IsActive: details.IsActive ? 1 : 2,
    // Convert comma-separated string back to array for multi-select
    DestinationIDs: details.DestinationIDs
      ? details.DestinationIDs.split(",").map((id: string) => id.trim())
      : []
  } : {
    IsActive: 1,
    DestinationIDs: [],
    ServiceInfo: [{ ServiceName: "", Description: "" }]
  };

  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={initialValues}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 10 }}
                gap={{ rows: 0, cols: 10 }}
                cols={[{ width: "50%" }, { width: "50%" }]}
              >
                <GridLayoutItem colSpan={2}>
                  <Typography.h4>
                    {ServiceLocationID ? "Update Service Location" : "Add Service Location"}
                  </Typography.h4>
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="CityName"
                    label="City Name"
                    placeholder="City Name"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="DestinationIDs"
                    label="Destinations"
                    placeholder="Select Destinations"
                    component={FormMultiSelectionFiled}
                    validator={requiredValidator}
                    astrike={true}
                    options={SectorList?.map((sector: any) => ({
                      value: sector.ID.toString(),
                      label: sector.SectorName,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem>
                  <Field
                    name="IsActive"
                    label="Status"
                    astrike={true}
                    component={FormSelectionField}
                    validator={requiredValidator}
                    options={STATUSARRAY?.map((status: any) => ({
                      value: status?.value,
                      label: status?.label,
                    }))}
                  />
                </GridLayoutItem>

                <GridLayoutItem colSpan={2} style={{ marginTop: 20 }}>
                  <Typography.h5>Service Information</Typography.h5>
                  <FieldArray name="ServiceInfo" component={ServiceInfoArray} />
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={2}
                  style={{
                    display: "flex",
                    justifyContent: "end",
                    alignItems: "end",
                    marginTop: 20,
                  }}
                >
                  <ButtonForAll
                    label={ServiceLocationID ? "Update" : "Save"}
                    type="submit"
                    disabled={!formRenderProps.allowSubmit || loading}
                  />

                  <RippleButton
                    type="button"
                    fillMode="outline"
                    themeColor="primary"
                    style={{ marginLeft: 10 }}
                    onClick={() => navigate("/servicelocation")}
                  >
                    Cancel
                  </RippleButton>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateServiceLocation;
